import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';

import { COLORS } from './constants';
import { ShimmerLine } from './LoadingAnimations';
import { Tip } from './Tip';

import { Issue, IssueUrgency } from '@/models/Issue';
import { VALUE_FONT_SMALL } from '@/theme/consts';
import { getAverageIssueCost } from '@/utils/issues';

const REPAIR_RATIO_TIP =
  'Estimated repair cost as a percentage of list price. Industry benchmark: under 3% is move-in ready, 3–8% is typical, over 8% is high burden.';

type BucketId = 'now' | 'soon' | 'mid' | 'future';

interface BucketDef {
  id: BucketId;
  label: string;
  sub: string;
  days: number;
}

const BUCKETS: BucketDef[] = [
  { id: 'now', label: 'Now', sub: '< 30 days', days: 30 },
  { id: 'soon', label: 'Soon', sub: '1 – 3 mo', days: 60 },
  { id: 'mid', label: 'Mid', sub: '3 – 12 mo', days: 275 },
  { id: 'future', label: 'Later', sub: '1 yr+', days: 365 },
];

const URGENCY_COLOR: Record<IssueUrgency, string> = {
  [IssueUrgency.Critical]: COLORS.critical,
  [IssueUrgency.Urgent]: COLORS.urgent,
  [IssueUrgency.Moderate]: COLORS.moderate,
  [IssueUrgency.Low]: COLORS.low,
  [IssueUrgency.Monitor]: COLORS.monitor,
};

interface PreparedIssue {
  id: string;
  title: string;
  category: string;
  urgency: IssueUrgency;
  cost: number;
  costMin?: number;
  costMax?: number;
  bucket: BucketId;
}

// Urgency → time-to-remediate, per URGENCY_TOOLTIPS in constants.ts:
//   Critical = address before moving in       → Now    (< 30 days)
//   Urgent   = within the first 30 days       → Soon   (1 – 3 mo)
//   Moderate = within the first year          → Mid    (3 – 12 mo)
//   Low      = at your convenience            → Later  (1 yr+)
//   Monitor  = may need attention in 2–5 yrs  → Later  (1 yr+)
const URGENCY_BUCKET: Record<IssueUrgency, BucketId> = {
  [IssueUrgency.Critical]: 'now',
  [IssueUrgency.Urgent]: 'soon',
  [IssueUrgency.Moderate]: 'mid',
  [IssueUrgency.Low]: 'future',
  [IssueUrgency.Monitor]: 'future',
};

const bucketForIssue = (issue: Issue): BucketId => URGENCY_BUCKET[issue.urgency] ?? 'future';

const formatK = (value: number): string => {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
};

interface TooltipState {
  issue: PreparedIssue | null;
  x: number;
  y: number;
}

// A repair's hover card (the HomeDNA tip): urgency's hairline and the category as the eyebrow, the title, then the cost
// range as a light figure with the estimate as meta. It opens below the cursor, or above it in the lower part of the
// window so it never runs off the bottom
const FloatingTooltip = ({ state }: { state: TooltipState }) => {
  if (!state.issue) return null;
  const it = state.issue;
  const PAD = 12;
  const TIP_WIDTH = Math.min(280, window.innerWidth - PAD * 2);
  const left = Math.max(PAD, Math.min(state.x + 14, window.innerWidth - TIP_WIDTH - PAD));
  const openUp = state.y > window.innerHeight * 0.6;
  const vertical = openUp
    ? { bottom: Math.max(PAD, window.innerHeight - state.y + 14) }
    : { top: Math.max(PAD, state.y + 14) };
  const isBigTicket = it.cost >= 5000;
  return createPortal(
    <div
      style={{
        position: 'fixed',
        left,
        ...vertical,
        zIndex: 9999,
        width: TIP_WIDTH,
        padding: '12px 14px',
        borderRadius: '12px',
        background: 'var(--tx-glass)',
        backdropFilter: 'var(--tx-blur)',
        WebkitBackdropFilter: 'var(--tx-blur)',
        boxShadow: 'var(--tx-shadow-sm)',
        pointerEvents: 'none',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginBottom: 6 }}>
        <span style={{ width: 2, height: 12, borderRadius: 1, background: URGENCY_COLOR[it.urgency] }} />
        <span>
          {it.urgency} · {it.category}
          {isBigTicket && <span style={{ color: COLORS.nhdAccent }}> · Big ticket</span>}
        </span>
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.35,
          color: 'var(--tx-text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
        {it.title}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginTop: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 300, letterSpacing: '-0.02em', lineHeight: 1, color: 'var(--tx-text)' }}>
          {it.costMin != null || it.costMax != null
            ? `$${(it.costMin ?? 0).toLocaleString()}–$${(it.costMax ?? 0).toLocaleString()}`
            : `$${it.cost.toLocaleString()}`}
        </span>
        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>
          Est. impact ${it.cost.toLocaleString()}
        </span>
      </div>
    </div>,
    document.body,
  );
};

// The horizon at a glance (the HomeDNA family): a column per window, each a light figure over its label and a hairline
// tick per repair, height by cost. Only Now carries colour, the one window that asks for action; hover a tick for it.
// a stable pseudo-random key per id (FNV-1a), so a scattered order holds still between renders
const scatterKey = (id: string): number => {
  let h = 2166136261;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// (Replaces the bubble chart, kept in git on develop; see the project memory "repair-horizon-bubbles-asset")
const HorizonTicks = ({
  bucketed,
  setTooltip,
  isMobile,
}: {
  bucketed: Record<BucketId, PreparedIssue[]>;
  setTooltip: (t: TooltipState) => void;
  isMobile: boolean;
}) => {
  const maxCost = Math.max(1, ...BUCKETS.flatMap((b) => bucketed[b.id].map((i) => i.cost)));
  return (
    <div
      style={{
        flex: 1,
        display: 'grid',
        // each window as wide as its repairs need, never narrower than its figure and label; a phone can't fit four
        // 96px floors inside the card's padding, so there the four windows split the width evenly
        gridTemplateColumns: isMobile
          ? `repeat(${BUCKETS.length}, minmax(0, 1fr))`
          : BUCKETS.map((b) => `minmax(96px, ${Math.max(6, bucketed[b.id].length)}fr)`).join(' '),
        // the card's own 20px on every side: the strips run edge to edge inside it, and its bottom is where they sit
        padding: isMobile ? '4px 20px 0' : '6px 20px 0',
      }}>
      {BUCKETS.map((b, bi) => {
        // every repair in its own window, evenly spaced across it, in its urgency colour with height by cost; the order
        // is mixed (seeded by id, steady between visits) so lengths and colours vary as repairs really arrive
        const items = [...bucketed[b.id]].sort((x, y) => scatterKey(x.id) - scatterKey(y.id));
        const total = items.reduce((sum, i) => sum + i.cost, 0);
        return (
          <div
            key={b.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: 14,
              minWidth: 0,
              // gutters only between windows, so the first and last sit exactly on the card's padding
              paddingLeft: bi === 0 ? 0 : isMobile ? 10 : 16,
              paddingRight: bi === BUCKETS.length - 1 ? 0 : isMobile ? 10 : 16,
              borderLeft: bi === 0 ? 'none' : '1px solid var(--tx-line)',
            }}>
            <div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 300,
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                  color: b.id === 'now' && total > 0 ? COLORS.critical : 'var(--tx-text)',
                }}>
                {formatK(total)}
              </div>
              <div style={{ marginTop: 6, fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>
                {b.label}
                {/* a phone's narrow window puts the span on its own line rather than wrapping mid-phrase */}
                {isMobile ? (
                  <div style={{ color: 'var(--tx-faint)', marginTop: 2 }}>{b.sub}</div>
                ) : (
                  <span style={{ color: 'var(--tx-faint)' }}> · {b.sub}</span>
                )}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: items.length > 1 ? 'space-between' : 'flex-start',
                gap: 2,
                height: 44,
                borderBottom: '1px solid var(--tx-line)',
                overflow: 'hidden',
              }}>
              {items.map((issue) => (
                <span
                  key={issue.id}
                  onMouseEnter={(e) => setTooltip({ issue, x: e.clientX, y: e.clientY })}
                  onMouseMove={(e) => setTooltip({ issue, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTooltip({ issue: null, x: 0, y: 0 })}
                  style={{
                    width: 2,
                    minWidth: 1,
                    flexShrink: 1,
                    height: `${Math.max(14, Math.sqrt(issue.cost / maxCost) * 100)}%`,
                    borderRadius: '1px 1px 0 0',
                    background: URGENCY_COLOR[issue.urgency],
                    opacity: 0.85,
                  }}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};


export interface RepairHorizonCompactProps {
  issues?: Issue[];
  isLoading?: boolean;
  /** Repair-cost-to-list-price ratio as a percentage (e.g. 1.4 = 1.4%). Hidden if undefined. */
  repairPriceRatio?: number | null;
}

export const RepairHorizonCompact = ({
  issues,
  isLoading = false,
  repairPriceRatio,
}: RepairHorizonCompactProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tooltip, setTooltip] = useState<TooltipState>({ issue: null, x: 0, y: 0 });

  const prepared = useMemo<PreparedIssue[]>(
    () =>
      (issues ?? [])
        .filter((i) => !i.resolved)
        .map((i) => ({
          id: i.id,
          title: i.title,
          category: i.trade ?? i.category ?? 'Other',
          urgency: i.urgency,
          cost: getAverageIssueCost(i),
          costMin: i.costEstimateMin,
          costMax: i.costEstimateMax,
          bucket: bucketForIssue(i),
        }))
        .filter((i) => i.cost > 0),
    [issues],
  );

  const bucketed = useMemo<Record<BucketId, PreparedIssue[]>>(() => {
    const map: Record<BucketId, PreparedIssue[]> = {
      now: [],
      soon: [],
      mid: [],
      future: [],
    };
    prepared.forEach((i) => map[i.bucket].push(i));
    return map;
  }, [prepared]);

  const grandTotal = prepared.reduce((s, i) => s + i.cost, 0);
  const nearTermTotal = bucketed.now.concat(bucketed.soon).reduce((s, i) => s + i.cost, 0);

  if (isLoading) {
    return (
      <div
        style={{
          background: COLORS.card,
          borderRadius: 25,
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          overflow: 'hidden',
          padding: '20px 0',
        }}>
        <div style={{ padding: '0 20px 14px' }}>
          <div style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)', marginBottom: 6 }}>
            Repair horizon
          </div>
          <ShimmerLine width={260} height={12} />
        </div>
        <div style={{ padding: '0 20px' }}>
          <ShimmerLine width="100%" height={140} borderRadius={12} />
        </div>
      </div>
    );
  }

  const hasIssues = prepared.length > 0;

  return (
    <div
      style={{
        padding: '20px 0',
        background: COLORS.card,
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        borderRadius: '24px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
      }}>
      {/* Top */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'flex-start',
          marginBottom: 16,
          padding: '0 20px',
          gap: isMobile ? 12 : 16,
          flexWrap: 'wrap',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)' }}>Repair horizon</div>
          <RouterLink
            to="/dashboard/repair-planner"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--tx-muted)',
              textDecoration: 'none',
            }}>
            Repair planner →
          </RouterLink>
        </div>
        <div
          style={{
            display: 'flex',
            gap: isMobile ? 16 : 20,
            alignSelf: isMobile ? 'stretch' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            flexWrap: 'wrap',
          }}>
          <div style={{ textAlign: 'left' }}>
            <div
              style={{
                fontFamily: VALUE_FONT_SMALL,
                fontSize: isMobile ? 17 : 20,
                fontWeight: 200,
                letterSpacing: '-0.04em',
                lineHeight: 1,
                color: COLORS.text,
              }}>
              ${grandTotal.toLocaleString()}
            </div>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 6 }}>Total est. cost</div>
          </div>
          {repairPriceRatio != null && (
            <div style={{ textAlign: 'left' }}>
              <div
                style={{
                  fontFamily: VALUE_FONT_SMALL,
                  fontSize: isMobile ? 17 : 20,
                  fontWeight: 200,
                  letterSpacing: '-0.04em',
                  lineHeight: 1,
                  color: COLORS.text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}>
                {repairPriceRatio.toFixed(1)}%
                <Tip text={REPAIR_RATIO_TIP} style={{ borderBottom: 'none', lineHeight: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 13, color: COLORS.accent2, opacity: 0.7 }} />
                </Tip>
              </div>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 6 }}>Repair / price</div>
            </div>
          )}
          <div style={{ textAlign: 'left' }}>
            <div
              style={{
                fontFamily: VALUE_FONT_SMALL,
                fontSize: isMobile ? 17 : 20,
                fontWeight: 200,
                letterSpacing: '-0.04em',
                lineHeight: 1,
                color: COLORS.urgent,
              }}>
              ${nearTermTotal.toLocaleString()}
            </div>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 6 }}>Due in 90 days</div>
          </div>
        </div>
      </div>

      {!hasIssues ? (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            fontSize: 12,
            color: COLORS.textDim,
          }}>
          No repair items yet.
        </div>
      ) : (
        <>
          <HorizonTicks bucketed={bucketed} setTooltip={setTooltip} isMobile={isMobile} />
        </>
      )}

      <FloatingTooltip state={tooltip} />
    </div>
  );
};

export default RepairHorizonCompact;
